"use client"

import { useEffect, useRef, useState } from "react"
import Link from "next/link"
import { useMutation, useQuery } from "@tanstack/react-query"
import { Bell, Loader2 } from "lucide-react"
import { toast } from "sonner"

import { queryClient, trpc } from "@/lib/trpc/client"

// Sino de notificações persistentes (badge de não-lido + painel + toast em tempo real).
// Vive dentro do TRPCProvider (Header foi movido para dentro dele no layout do app).
const POLL_MS = 60_000

export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const prevUnread = useRef<number | null>(null)

  const { data: unread = 0 } = useQuery({
    ...trpc.notifications.unreadCount.queryOptions(),
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
  })

  const { data: items = [], isLoading } = useQuery({
    ...trpc.notifications.list.queryOptions({ limit: 20 }),
    refetchInterval: POLL_MS,
  })

  const markRead = useMutation(trpc.notifications.markRead.mutationOptions())
  const markAllRead = useMutation(
    trpc.notifications.markAllRead.mutationOptions()
  )

  const invalidate = (): void => {
    queryClient.invalidateQueries({
      queryKey: trpc.notifications.unreadCount.queryKey(),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.notifications.list.queryKey({ limit: 20 }),
    })
  }

  // Toast quando a contagem de não-lidas aumenta (aluno online recebe o alerta na hora).
  useEffect(() => {
    if (prevUnread.current !== null && unread > prevUnread.current) {
      const newest = items.find((n) => n.readAt == null)
      toast(newest?.title ?? "Você tem uma nova notificação", {
        description: newest?.body ?? undefined,
      })
    }
    prevUnread.current = unread
  }, [unread, items])

  // Fecha o painel ao clicar fora.
  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent): void => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", onClick)
    return () => document.removeEventListener("mousedown", onClick)
  }, [open])

  const handleItemClick = async (id: string): Promise<void> => {
    await markRead.mutateAsync({ id })
    invalidate()
    setOpen(false)
  }

  const handleMarkAll = async (): Promise<void> => {
    await markAllRead.mutateAsync(undefined)
    invalidate()
  }

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Notificações"
        className="relative flex h-[34px] w-[34px] items-center justify-center rounded-full text-text-600 transition-colors hover:bg-background-100 hover:text-text-900"
      >
        <Bell className="h-[18px] w-[18px]" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-alert px-1 text-[10px] font-bold text-white">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-xl border border-background-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-background-100 px-4 py-2.5">
            <span className="text-sm font-semibold text-text-900">
              Notificações
            </span>
            {unread > 0 && (
              <button
                onClick={handleMarkAll}
                disabled={markAllRead.isPending}
                className="text-body-sm font-medium text-primary-app-600 hover:text-primary-app-700 disabled:opacity-50"
              >
                Marcar todas como lidas
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-4 w-4 animate-spin text-text-400" />
              </div>
            ) : items.length === 0 ? (
              <p className="text-body-sm px-4 py-8 text-center text-text-400">
                Nenhuma notificação.
              </p>
            ) : (
              items.map((n) => {
                const body = (
                  <div
                    className={`flex flex-col gap-0.5 border-b border-background-50 px-4 py-3 text-left transition-colors hover:bg-background-50 ${
                      n.readAt == null ? "bg-primary-app-50/40" : ""
                    }`}
                  >
                    <span className="text-body-sm font-medium text-text-900">
                      {n.title}
                    </span>
                    {n.body && (
                      <span className="text-body-sm line-clamp-2 text-text-500">
                        {n.body}
                      </span>
                    )}
                  </div>
                )
                return n.link ? (
                  <Link
                    key={n.id}
                    href={n.link}
                    onClick={() => handleItemClick(n.id)}
                    className="block"
                  >
                    {body}
                  </Link>
                ) : (
                  <button
                    key={n.id}
                    onClick={() => handleItemClick(n.id)}
                    className="block w-full"
                  >
                    {body}
                  </button>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}
